<template>
  <div class="profile-flyout-contact-fi">
    <div class="profile-flyout-contact-fi__headline">{{ sitecoreData.ContactsHeadline }}</div>

    <div
      v-if="sitecoreData.EnableSupportBoxWithContacts"
      class="profile-flyout-contact-fi__contacts"
    >
      <div
        v-for="(person, idx) in contactPersons"
        :key="`contact-person-${idx}`"
        class="profile-flyout-contact-fi__contact"
      >
        <div class="profile-flyout-contact-fi__contact__person">
          <div
            v-if="getRole(person.partnerRole)"
            class="profile-flyout-contact-fi__contact__role"
          >
            {{ getRole(person.partnerRole) }}
          </div>
          <div class="profile-flyout-contact-fi__contact__name">{{ person.fullName }}</div>
        </div>
        <div class="profile-flyout-contact-fi__contact__data">
          <a
            v-if="person.phone"
            class="profile-flyout-contact-fi__contact__phone"
            :href="getTel(person.phone)"
          >
            <icon-fi icon="phone-call" /> {{ person.phone }}
          </a>
          <a
            v-if="person.email"
            class="profile-flyout-contact-fi__contact__mail"
            :href="getMailto(person.email)"
          >
            <icon-fi icon="mail" /> {{ person.email }}
          </a>
        </div>
      </div>
    </div>

    <div
      v-else
      class="profile-flyout-contact-fi__contact"
    >
      <div class="profile-flyout-contact-fi__contact__data">
        <a
          class="profile-flyout-contact-fi__contact__phone"
          :href="getTel(sitecoreData.ContactPhoneNumber)"
        >
          <icon-fi icon="phone-call" /> {{ sitecoreData.ContactPhoneNumber }}
        </a>
        <a
          class="profile-flyout-contact-fi__contact__mail"
          :href="getMailto(sitecoreData.ContactEmailAddress)"
        >
          <icon-fi icon="mail" /> {{ sitecoreData.ContactEmailAddress }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import { IconFi } from 'atoms';
import { ref } from 'vue';

import { useFischer } from '@/composables/fischerPlugin';
import GETCONTACTPERSONS from '@/graphql/getContactPersons.gql';
import { getMailToHref, getTelHref } from '@/utils/linkHelper';

import type { ProfileFlyoutData } from '../profile-flyout-types';

defineOptions({ name: 'ProfileFlyoutContactFi' });

const props = defineProps<{
  sitecoreData: ProfileFlyoutData;
}>();

const fischer = useFischer();
const handle = useClientHandle();

const maxContacts = 3;

const contactPersons = ref<any[]>([]);

const getTel = (tel: string) => getTelHref(tel);

const getMailto = (mail: string) => getMailToHref(mail);

const getRole = (role: string) => {
  if (role === 'EXTERNAL_SALES') {
    return props.sitecoreData.ContactRoleExternalSales;
  } else if (role === 'INTERNAL_SALES') {
    return props.sitecoreData.ContactRoleInternalSales;
  }

  return null;
};

const getContactPersons = async () => {
  const assistedService = fischer.user.getAssistedService();
  if (
    !props.sitecoreData.EnableSupportBoxWithContacts ||
    (assistedService && !assistedService.user)
  ) {
    return;
  }

  const { data, error } = await handle.client
    .query(GETCONTACTPERSONS, {
      baseSiteId: fischer.baseSiteId(),
      accessToken: fischer.user.accessToken(),
      userId: fischer.user.id(),
    })
    .toPromise();
  // urql resolves with { data, error } rather than throwing
  if (error) {
    console.error(error);
    return;
  }
  const persons = data?.getContactPersons;
  if (!persons) {
    return;
  }
  const removeNotContactableContactPersons = (contacts: any[]) =>
    contacts.filter((contactPerson) => contactPerson.email || contactPerson.phone);

  contactPersons.value = removeNotContactableContactPersons(persons).slice(0, maxContacts);
};

// created
getContactPersons();
</script>

<style scoped src="./profile-flyout-contact-fi" />
